/**
 * Pure tab-schema utilities.
 *
 * Active runtime code resolves `DocumentSchemaConfig` objects from the compiled
 * template payload, then uses the helpers here for document initialization,
 * hydration, and form extraction.
 */

import type { JSONContent } from "@tiptap/react";

import { serializeToMarkdown } from "../../conversion/serialize-markdown";
import {
  assertDocumentMatchesSchema,
  canonicalizeStoredDocumentFieldAttrs,
} from "./schema-metadata";
import type { DocumentSchemaConfig, ExtractedFormData } from "./types";

/**
 * Extract form data from a single tab document using its schema.
 */
export function extractTabFormData(
  schema: DocumentSchemaConfig,
  doc: JSONContent
): ExtractedFormData {
  const validFieldIds = new Set(schema.fields.map((f) => f.fieldId));
  const result: ExtractedFormData = {};

  if (!doc.content) return result;

  for (const node of doc.content) {
    const fieldId = node.attrs?.fieldId as string | undefined;
    if (!fieldId || !validFieldIds.has(fieldId)) continue;

    switch (node.type) {
      case "sectionTitle":
        result[fieldId] = extractTextContent(node);
        break;
      case "paragraphSection":
      case "richSection":
        result[fieldId] = node.content ? serializeToMarkdown(node.content).trimEnd() : "";
        break;
      case "atomicField":
        result[fieldId] = (node.attrs?.value as string) ?? "";
        break;
    }
  }

  return result;
}

/**
 * Create initial tab document content from a schema, optionally populated with existing data.
 */
export function createTabInitialContent(
  schema: DocumentSchemaConfig,
  existingData?: Record<string, unknown>
): JSONContent {
  return {
    type: "doc",
    content: schema.fields.map((field) => createFieldNode(field, existingData)),
  };
}

// ─── Internal Helpers ───────────────────────────────────────────────────────

/** Generate an empty document for a tab schema. */
function createEmptyTabDocument(schema: DocumentSchemaConfig): JSONContent {
  return createTabInitialContent(schema);
}

export function hydrateTabDocument(
  schema: DocumentSchemaConfig,
  existing?: JSONContent | null
): JSONContent {
  if (existing) {
    return assertDocumentMatchesSchema(
      schema,
      canonicalizeStoredDocumentFieldAttrs(schema, reconcileStoredDocumentFields(schema, existing))
    );
  }
  return createEmptyTabDocument(schema);
}

function reconcileStoredDocumentFields(
  schema: DocumentSchemaConfig,
  existing: JSONContent
): JSONContent {
  if (existing.type !== "doc" || !Array.isArray(existing.content)) {
    return existing;
  }

  if (existing.content.length === schema.fields.length) {
    return existing;
  }

  const fieldsById = new Set(schema.fields.map((field) => field.fieldId));
  const existingByFieldId = new Map<string, JSONContent>();

  for (const node of existing.content) {
    const fieldId = typeof node.attrs?.fieldId === "string" ? node.attrs.fieldId : "";
    if (!fieldId || !fieldsById.has(fieldId) || existingByFieldId.has(fieldId)) {
      return existing;
    }
    existingByFieldId.set(fieldId, node);
  }

  return {
    ...existing,
    content: schema.fields.map(
      (field) => existingByFieldId.get(field.fieldId) ?? createFieldNode(field)
    ),
  };
}

/** Build a single field node from its config. */
function createFieldNode(
  field: {
    nodeType: string;
    fieldId: string;
    label: string;
    required?: boolean;
    guidance?: string;
    chromeless?: boolean;
    expectedMentionTypes?: string[];
    fieldType?: string;
    options?: string[];
  },
  existingData?: Record<string, unknown>
): JSONContent {
  const existing = existingData?.[field.fieldId];
  const baseAttrs = {
    fieldId: field.fieldId,
    label: field.label,
    guidance: field.guidance,
    ...(field.chromeless === true ? { chromeless: true } : {}),
  };

  switch (field.nodeType) {
    case "sectionTitle":
      return {
        type: "sectionTitle",
        attrs: baseAttrs,
        content: existing ? [{ type: "text", text: String(existing) }] : undefined,
      };

    case "paragraphSection": {
      let content: JSONContent[];
      if (isJSONContentArray(existing)) {
        content = existing;
      } else if (existing) {
        content = [{ type: "paragraph", content: [{ type: "text", text: String(existing) }] }];
      } else {
        content = [{ type: "paragraph" }];
      }
      return { type: "paragraphSection", attrs: baseAttrs, content };
    }

    case "richSection": {
      let content: JSONContent[];
      if (isJSONContentArray(existing)) {
        content = existing;
      } else if (existing) {
        content = [{ type: "paragraph", content: [{ type: "text", text: String(existing) }] }];
      } else {
        content = [{ type: "paragraph" }];
      }
      return { type: "richSection", attrs: baseAttrs, content };
    }

    case "atomicField":
      return {
        type: "atomicField",
        attrs: {
          ...baseAttrs,
          fieldType: field.fieldType ?? "text",
          options: field.options ?? [],
          value: existing != null ? String(existing) : "",
        },
      };

    default:
      return { type: "paragraph" };
  }
}

/** Recursively extract plain text from a node tree. */
function extractTextContent(node: JSONContent): string {
  if (node.text) return node.text;
  if (!node.content) return "";
  return node.content.map(extractTextContent).join("");
}

/** Check if a value is a Tiptap JSONContent array. */
function isJSONContentArray(value: unknown): value is JSONContent[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === "object" &&
    value[0] !== null &&
    "type" in value[0]
  );
}
